/**
 * Navbar control for switching between light and dark.
 *
 * Icon-only, because the navbar is already carrying a brand, six section links
 * and a sign-out. The icon shows the theme you would get by pressing it, not
 * the one you are in — the same convention as every OS display setting, and the
 * reason the `aria-label` spells the action out rather than the state.
 */

import { Icon, Moon, Sun } from "@norican/icons";
import { Toggle } from "@norican/react";

import { useTheme } from "@/contexts/theme";

export function ThemeToggle() {
  const { theme, setTheme, followsSystem } = useTheme();
  const nextTheme = theme === "dark" ? "light" : "dark";

  const label = `Switch to ${nextTheme} mode${followsSystem ? " (currently following your system setting)" : ""}`;

  return (
    <Toggle
      variant="ghost"
      size="sm"
      shape="pill"
      // Controlled: the theme is owned by the provider, not by this button, so
      // two toggles (or an OS change) can never disagree about what is pressed.
      pressed={theme === "dark"}
      onPressedChange={() => setTheme(nextTheme)}
      aria-label={label}
      title={label}
    >
      {/* Decorative by default (Icon adds `aria-hidden` when no `label` is
          passed) — the Toggle's own aria-label carries the meaning. */}
      <Icon icon={nextTheme === "dark" ? Moon : Sun} size="sm" />
    </Toggle>
  );
}
